import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import { AppStripItemConfig } from "./appStripTypes";
import { setActiveApp, AppId } from "@/app/globalSlices/appStripSlice";

interface AppStripItemWithMenuProps {
  item: AppStripItemConfig;
  isActive: boolean;
  onMenuItemClick?: () => void;
}

export default function AppStripItemWithMenu({
  item,
  isActive,
  onMenuItemClick,
}: AppStripItemWithMenuProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const Icon = item.icon;
  const [open, setOpen] = React.useState(false);

  const handleMenuItemClick = (href: string) => {
    // Set active app when navigating through menu
    dispatch(setActiveApp(item.id as AppId));
    navigate(href);
    setOpen(false);
    onMenuItemClick?.();
  };

  return (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={item.name}
              className={cn(
                "relative flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 transition-all duration-200",
                "hover:bg-gray-200 dark:hover:bg-white/10",
                open || isActive
                  ? "bg-gray-200 text-gray-900 dark:bg-white/15 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white",
              )}
              type="button"
            >
              {/* Active indicator bar */}
              {(open || isActive) && (
                <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
              )}
              <Icon className="h-5 w-5" />
              <span className="w-full truncate text-center text-[10px] font-medium leading-tight">
                {item.shortLabel ?? item.name}
              </span>
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent align="center" side="right" sideOffset={8}>
          <span className="text-xs font-medium">{item.name}</span>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="start"
        className="w-64 bg-slate-800 border-slate-700"
        side="right"
        sideOffset={12}
      >
        {item.menuOptions?.map((option) => (
          <DropdownMenuItem
            className="cursor-pointer focus:bg-slate-700 focus:text-white"
            key={option.href}
            onClick={() => handleMenuItemClick(option.href)}
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-white">
                {option.label}
              </span>
              {option.description && (
                <span className="text-xs text-slate-400">
                  {option.description}
                </span>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
